import { useTranslation } from 'next-i18next';
import {
  Input, Label, Select, DecorativeGroup, FormError,
} from '@components/common/Forms';
import { Dispatch, SetStateAction } from 'react';

/** Props */
interface PhoneNumberGroupProps {
  phonePrefixes?: ApiSchema<'OptionItem2'>[],
  selectedCountryCode?: string,
  setSelectedCountryCode?: Dispatch<SetStateAction<string>>,
  phoneNumber?: string,
  setPhoneNumber?: Dispatch<SetStateAction<string>>,
  phoneValidityStates?: { isInvalid: boolean; errorMSG: string; },
  setPhoneValidityStates?: Dispatch<SetStateAction<{ isInvalid: boolean; errorMSG: string; }>>,
}

/**
 * @description Phone number group component. It contains a country code select and a phone number input.
 * - `phonePrefixes` - The list of phone prefixes.
 * - `selectedCountryCode` - The selected country code.
 * - `setSelectedCountryCode` - The setter for the selected country code.
 * - `phoneNumber` - The phone number.
 * - `setPhoneNumber` - The setter for the phone number.
 * - `phoneValidityStates` - The phone number validity states.
 * - `setPhoneValidityStates` - The setter for the phone number validity states.
 */
export const PhoneNumberGroup = (props: PhoneNumberGroupProps) => {
  // Destructure props
  const {
    phonePrefixes,
    selectedCountryCode,
    setSelectedCountryCode,
    phoneNumber,
    setPhoneNumber,
    phoneValidityStates = { isInvalid: false, errorMSG: '' },
    setPhoneValidityStates,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Render component
  // **********************
  return (
    <>
      <Label
        htmlFor="customer.phone"
        description={t('page.company.onboard.form.phone.number')}
        descriptionClass="!mb-2 px-3.5 font-bold text-ink-medium"
        required
      >
        <DecorativeGroup
          className="flex max-h-13 rounded bg-surface p-1"
          hasError={phoneValidityStates?.isInvalid}
        >
          {/* Country code */}
          <Select
            className="w-auto shrink-0 py-0 pr-8"
            value={selectedCountryCode}
            onChange={(e) => {
              setSelectedCountryCode?.(e.target.value);
            }}
          >
            {phonePrefixes?.map((item) => (
              <option key={item.id} value={item.name}>
                {`+${item.id} (+${item.name})`}
              </option>
            ))}
          </Select>

          {/* Separator (form elements have no border inside the DecorativeGroup) */}
          <span className="border border-dashed" />

          {/* Phone number */}
          <Input
            required
            type="tel"
            minLength={7}
            maxLength={12}
            className="grow"
            withError={false}
            pattern="^\d{7,12}$"
            patternMismatchMessage={t('phone.number.input.pattern')}
            getValidityStates={setPhoneValidityStates}
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber?.(e.target.value);
            }}
          />
        </DecorativeGroup>

        {/* External FormError component */}
        <FormError
          isShowing={phoneValidityStates?.isInvalid}
          errorMessage={phoneValidityStates?.errorMSG}
        />
      </Label>
    </>
  );
};
